import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';

const OptionInlineForm = ({ option, questionId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    label: '',
    value_int: '',
    value_str: '',
    is_active: true,
    question: questionId,
  });
  const [errors, setErrors] = useState({});
  const [valueType, setValueType] = useState('int');

  useEffect(() => {
    if (option) {
      setFormData(option);
      if (option.value_str && !option.value_int) {
        setValueType('str');
      }
    }
  }, [option]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleValueTypeChange = (type) => {
    setValueType(type);
    if (type === 'int') {
      setFormData(prev => ({ ...prev, value_str: '', value_int: prev.value_int || '' }));
    } else {
      setFormData(prev => ({ ...prev, value_int: '', value_str: prev.value_str || '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (valueType === 'int' && !formData.value_int && formData.value_int !== 0) {
      newErrors.value_int = 'Integer value is required';
    }
    if (valueType === 'str' && !formData.value_str.trim()) {
      newErrors.value_str = 'String value is required';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length === 0) {
      const submitData = { ...formData };
      if (valueType === 'int') {
        submitData.value_str = '';
        submitData.value_int = parseInt(submitData.value_int);
      } else {
        submitData.value_int = null;
      }
      onSave(submitData);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="label"
            placeholder="e.g., Yes"
            value={formData.label}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.label ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm`}
          />
          {errors.label && <p className="text-red-500 text-xs mt-1">{errors.label}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Value Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleValueTypeChange('int')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                valueType === 'int'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Integer
            </button>
            <button
              type="button"
              onClick={() => handleValueTypeChange('str')}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                valueType === 'str'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              String
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          {valueType === 'int' ? 'Integer Value' : 'String Value'} <span className="text-red-500">*</span>
        </label>
        {valueType === 'int' ? (
          <input
            type="number"
            name="value_int"
            placeholder="e.g., 1"
            value={formData.value_int}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.value_int ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm`}
          />
        ) : (
          <input
            type="text"
            name="value_str"
            placeholder="e.g., Yes"
            value={formData.value_str}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.value_str ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm`}
          />
        )}
        {(errors.value_int || errors.value_str) && (
          <p className="text-red-500 text-xs mt-1">{errors.value_int || errors.value_str}</p>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
          id="is_active_opt_inline"
        />
        <label htmlFor="is_active_opt_inline" className="text-xs font-medium text-gray-700 cursor-pointer">
          Active Option
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 text-xs font-semibold hover:bg-gray-50 transition-colors flex items-center gap-1"
        >
          <X size={12} /> Cancel
        </button>
        <button
          type="submit"
          className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-shadow flex items-center gap-1"
        >
          <Save size={12} /> {option ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default OptionInlineForm;